// ROI Calculation Utilities
// Computes savings, investment, ROI, and payback based on captured assessment data

import { normalizePillarName } from '@shared/pillar-constants'

/**
 * Pillar Filtering Utility Functions
 *
 * These functions ensure financial calculations (ROI, NPV, Payback) only include
 * data from selected pillars, preventing inflated CFO-grade presentation numbers.
 */

/**
 * Filters pillar breakdown data to include only selected pillars
 * @param pillarBreakdown - Object containing all pillar data
 * @param selectedPillars - Array of selected pillar names
 * @returns Filtered pillar breakdown object
 */
export function filterBySelectedPillars(pillarBreakdown: Record<string, any>, selectedPillars: string[]): Record<string, any> {
  if (!pillarBreakdown || !selectedPillars || selectedPillars.length === 0) {
    console.warn('[PILLAR FILTER] Missing required data:', {
      hasPillarBreakdown: !!pillarBreakdown,
      selectedPillarsCount: selectedPillars?.length || 0
    })
    return {}
  }

  // Normalize selected pillars for consistent matching
  const normalizedSelectedPillars = selectedPillars.map(p => normalizePillarName(p))

  const filtered = Object.fromEntries(
    Object.entries(pillarBreakdown).filter(([pillarName]) => {
      const normalizedPillarName = normalizePillarName(pillarName)
      return normalizedSelectedPillars.includes(normalizedPillarName)
    })
  )

  console.log('[PILLAR FILTER] Applied selection filter:', {
    totalPillars: Object.keys(pillarBreakdown).length,
    selectedPillars: selectedPillars.length,
    normalizedSelectedPillars,
    filteredPillars: Object.keys(filtered).length,
    filteredPillarNames: Object.keys(filtered)
  })

  return filtered
}

/**
 * Calculates total annual savings from filtered pillar data
 * @param filteredPillarBreakdown - Pillar data filtered by selection
 * @returns Total annual savings from selected pillars only
 */
export function calculateFilteredAnnualSavings(filteredPillarBreakdown: Record<string, any>): number {
  if (!filteredPillarBreakdown) return 0

  const totalSavings = Object.values(filteredPillarBreakdown).reduce((sum, pillar: any) => {
    return sum + (pillar?.annualSavings || 0)
  }, 0)

  console.log('[PILLAR FILTER] Calculated filtered savings:', {
    pillarsIncluded: Object.keys(filteredPillarBreakdown),
    totalFilteredSavings: totalSavings
  })

  return totalSavings
}

interface FTEAllocationData {
  regions: Array<{
    regionName: string
    totalFTEs: number
    activityBreakdown: Record<string, number>
  }>
}

interface AutomationModelData {
  calculatedFTESaved: Record<string, Record<string, number>>
  totalFTESaved: number
  overrides: Record<string, number>
}

interface ROIData {
  totalSavings: number
  totalInvestment: number | null
  roiPercent: number | null
  paybackPeriodMonths: number | null
  fiveYearValue: number
  costSavingsOnly: boolean
  calculationDate: string
  breakdown: {
    annualFTESavings: number
    averageBlendedRate: number
    pillarInvestment: number | null
    implementationCost: number | null
  }
}

// REMOVED: Hardcoded FTE costs - now database-driven via customFTECosts parameter
// REMOVED: Hardcoded pillar investment costs - now database-driven via pricing system

// REMOVED: Emergency fallback - production must use authentic database data only
// No hardcoded FTE costs allowed - all must come from database

export function calculateROIData(
  fteAllocation: FTEAllocationData | null,
  automationModel: AutomationModelData | null,
  selectedPillars: string[] = [],
  pricingEnabled: boolean = false,
  customFTECosts?: Record<string, number>,
  pillarBreakdown?: Record<string, any>
): ROIData {
  console.log('[ROI CALC] Starting ROI calculation with inputs:', {
    hasFTEAllocation: !!fteAllocation,
    hasAutomationModel: !!automationModel,
    selectedPillarsCount: selectedPillars.length,
    pricingEnabled,
    hasPillarBreakdown: !!pillarBreakdown
  })

  // CRITICAL FIX: Apply pillar filtering to prevent inflated financial metrics
  if (pillarBreakdown && selectedPillars.length > 0) {
    console.log('[ROI CALC] PILLAR FILTERING ENABLED: Using only selected pillars for calculations')
    const filteredSavings = calculateFilteredAnnualSavings(filterBySelectedPillars(pillarBreakdown, selectedPillars))
    
    if (filteredSavings > 0) {
      console.log('[ROI CALC] Using filtered pillar savings instead of FTE allocation calculations:', {
        filteredAnnualSavings: filteredSavings,
        selectedPillars: selectedPillars
      })
      
      // Use filtered savings directly for accurate financial metrics
      const roiData: ROIData = {
        totalSavings: Math.round(filteredSavings),
        totalInvestment: null,
        roiPercent: null,
        paybackPeriodMonths: null,
        fiveYearValue: Math.round(filteredSavings * 5),
        costSavingsOnly: !pricingEnabled,
        calculationDate: new Date().toISOString(),
        breakdown: {
          annualFTESavings: 0, // Will be calculated from pillar data
          averageBlendedRate: 0,
          pillarInvestment: null,
          implementationCost: null
        }
      }

      console.log('[ROI CALC] Filtered ROI Data:', roiData)
      return roiData
    }
  }

  // Calculate annual FTE savings
  let totalAnnualSavings = 0
  let totalFTESaved = 0
  let weightedCostSum = 0

  if (fteAllocation && automationModel) {
    try {
      // Handle different FTE allocation data structures
      const regions = fteAllocation.fteAllocation?.regions || fteAllocation.regions || []
      
      regions.forEach((region: any) => {
        // Handle different region name formats in data
        const regionKey = region.regionName || region.name || region
        const regionFTECost = customFTECosts?.[regionKey] || 
                              customFTECosts?.[regionKey.toLowerCase()] ||
                              customFTECosts?.[regionKey.replace(/\s+/g, '_').toLowerCase()]
        
        console.log(`[ROI CALC] Region FTE cost lookup:`, {
          regionKey,
          availableCostKeys: Object.keys(customFTECosts || {}),
          foundCost: regionFTECost
        })
        
        if (!regionFTECost) {
          console.error(`[ROI CALC] CRITICAL: No database FTE cost found for region: ${regionKey}`)
          console.error('[ROI CALC] All FTE costs must come from database - no hardcoded fallbacks allowed')
          console.error('[ROI CALC] Available cost regions:', Object.keys(customFTECosts || {}))
          return // Skip this region - cannot calculate without authentic data
        }

        Object.entries(region.activityBreakdown || {}).forEach(([activity, ftes]) => {
          // CORRECT FORMULA: FTE per region × automation rate per activity × FTE cost per region
          const automationRate = automationModel.overrides?.[activity] || 0
          const ftesNumber = typeof ftes === 'number' ? ftes : parseFloat(ftes) || 0
          const automationDecimal = automationRate / 100 // Convert percentage to decimal
          const ftesSaved = ftesNumber * automationDecimal
          const activitySavings = ftesSaved * regionFTECost
          
          console.log(`[ROI CALC] ${region.regionName} - ${activity}:`, {
            fteAllocated: ftesNumber,
            automationRate: `${automationRate}%`,
            automationDecimal,
            ftesSaved: Math.round(ftesSaved * 100) / 100,
            regionFTECost: `$${regionFTECost.toLocaleString()}`,
            activitySavings: `$${Math.round(activitySavings).toLocaleString()}`,
            formula: `${ftesNumber} FTE × ${automationRate}% × $${regionFTECost.toLocaleString()}`
          })
          
          totalAnnualSavings += activitySavings
          totalFTESaved += ftesSaved
          weightedCostSum += ftesSaved * regionFTECost
        })
      })
    } catch (error) {
      console.error('[ROI CALC] Error processing FTE allocation or automation data:', error)
    }
  } else {
    console.warn('[ROI CALC] Missing FTE allocation or automation model data', {
      hasFTEAllocation: !!fteAllocation,
      hasAutomationModel: !!automationModel,
      fteAllocationType: typeof fteAllocation,
      automationModelType: typeof automationModel
    })
  }

  const averageBlendedRate = totalFTESaved > 0 ? weightedCostSum / totalFTESaved : 0

  console.log('[ROI CALC] Savings calculation:', {
    totalAnnualSavings,
    totalFTESaved,
    averageBlendedRate
  })

  // Calculate investment and ROI (if pricing enabled)
  let totalInvestment: number | null = null
  let roiPercent: number | null = null
  let paybackPeriodMonths: number | null = null
  let pillarInvestment: number | null = null
  let implementationCost: number | null = null

  if (pricingEnabled && selectedPillars.length > 0) {
    // CRITICAL FIX: Investment costs must come from pricing system, not hardcoded values
    console.warn('[ROI CALC] Pricing-enabled mode requires pricingData parameter for authentic calculations')
    console.warn('[ROI CALC] No hardcoded investment costs will be used - returning zero until pricing data provided')
    
    // Investment will be calculated by pricing system, not here
    pillarInvestment = 0
    implementationCost = 0
    totalInvestment = 0
    
    // ROI calculations will be handled by pricing-aware components with authentic data
    roiPercent = null
    paybackPeriodMonths = null

    console.log('[ROI CALC] Investment calculation deferred to pricing system:', {
      pillarInvestment,
      implementationCost,
      totalInvestment,
      note: 'Use pricing calculator for authentic investment costs'
    })
  }

  // Calculate 5-year cumulative value
  const fiveYearValue = pricingEnabled && totalInvestment
    ? (totalAnnualSavings * 5) - totalInvestment
    : totalAnnualSavings * 5

  const roiData: ROIData = {
    totalSavings: Math.round(totalAnnualSavings),
    totalInvestment,
    roiPercent,
    paybackPeriodMonths,
    fiveYearValue: Math.round(fiveYearValue),
    costSavingsOnly: !pricingEnabled,
    calculationDate: new Date().toISOString(),
    breakdown: {
      annualFTESavings: Math.round(totalFTESaved * 100) / 100,
      averageBlendedRate: Math.round(averageBlendedRate),
      pillarInvestment,
      implementationCost
    }
  }

  console.log("✅ ROI Data:", roiData)

  return roiData
}

export function formatCurrency(amount: number | null, currency: string = 'USD'): string {
  if (amount === null || amount === undefined) return 'N/A'
  
  // Handle negative numbers by preserving the sign
  const isNegative = amount < 0
  const absAmount = Math.abs(amount)
  
  // Format millions with M suffix for better readability
  if (absAmount >= 1000000) {
    return `${isNegative ? '-' : ''}$${(absAmount / 1000000).toFixed(1)}M`
  } else if (absAmount >= 1000) {
    return `${isNegative ? '-' : ''}$${(absAmount / 1000).toFixed(0)}K`
  }
  
  // For amounts under 1000, still use standard currency format
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })
  
  return formatter.format(amount)
}

export function formatPercentage(percent: number | null): string {
  if (percent === null || percent === undefined) return 'N/A'
  return `${percent}%`
}

export function formatMonths(months: number | null): string {
  if (months === null || months === undefined) return 'N/A'
  
  if (months < 12) {
    return `${months} months`
  } else {
    const years = Math.floor(months / 12)
    const remainingMonths = months % 12
    if (remainingMonths === 0) {
      return `${years} ${years === 1 ? 'year' : 'years'}`
    } else {
      return `${years}.${Math.round(remainingMonths)} years`
    }
  }
}